import * as ReactQuery from 'react-query';
import React from 'react';
import { useNotifications } from 'components/notifications/notifications';
import { ErrorType } from 'utils/http';
import { CurrentAuthenticatedUserError } from 'utils/auth/errors';
import { LocaleKey } from 'translations';

import * as NotificationsService from './notifications';
import * as AdminService from './admin';

export { NotificationsService, AdminService };

/*
 * The error toast renders its `description` through react-intl, which expects a
 * string translation key. Some backend errors (most notably JSON-schema
 * validation failures) arrive with a non-string `message` - e.g. the raw AJV
 * payload `{ errors: [...] }`. Passing that object straight to the toast makes
 * react-intl coerce it to the literal string "[object Object]".
 *
 * This normalizes the error into a safe translation key: a usable string
 * message is kept as-is, anything else falls back to the generic error copy.
 * */
function resolveErrorDescription(error: unknown): LocaleKey {
  const message = (error as ErrorType | undefined)?.message;

  if (typeof message === 'string' && message.length > 0) {
    return message as LocaleKey;
  }

  return 'errors.defaultMessage';
}

const queryClient = new ReactQuery.QueryClient({
  defaultOptions: {
    queries: {
      // TODO: configure optimal values for caching
      // staleTime: Infinity, // Change to milliseconds in case you want to refetch data in the background
      // cacheTime: Infinity, // Change to milliseconds in case you want to garbage collect unused data
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

export const ServiceProvider: React.FC = ({ children }) => {
  const notifications = useNotifications();

  queryClient.setDefaultOptions({
    queries: {
      onError: (error) => {
        if (error == null || !shouldShowNotification(error as Error)) {
          return;
        }

        notifications.error({
          description: resolveErrorDescription(error),
        });
      },
    },
    mutations: {
      onError: (error) => {
        if (error == null || !shouldShowNotification(error as Error)) {
          return;
        }

        notifications.error({
          description: resolveErrorDescription(error),
        });
      },
    },
  });

  return (
    <ReactQuery.QueryClientProvider client={queryClient}>
      {children}
    </ReactQuery.QueryClientProvider>
  );
};

function shouldShowNotification(error: Error) {
  if (error instanceof CurrentAuthenticatedUserError) {
    return false;
  }

  return true;
}
